import type { MemoSummary, ProjectView } from "@portal/schemas";
import { Badge, Button } from "@portal/ui";
import { Pin, Plus } from "lucide-react";
import { useState } from "react";
import { useProjectMemos, usePrefetchMemo } from "../../api/queries";
import { CardStatus } from "../../components/cards/CardStatus";
import { HomeSection } from "../../components/HomeSection";
import { MemoDialog } from "../memos/MemoDialog";
import { MemoIcon } from "../memos/MemoIcon";

const BOARD_HINT =
  "このプロジェクトに初めて参加した人に、最初に見てほしいメモを留めておく場所です。参加者は、プロジェクトのメモを開いて「ボードに留める」で追加できます。";

/** ピンの、自分にとっての状態の印。 */
export const READ_STATE_LABELS = { UNREAD: "未読", UPDATED: "更新" } as const;

/** ボードに出すメモ(ピン)。留めた順。 */
export function pinnedMemos(memos: readonly MemoSummary[]): MemoSummary[] {
  return memos
    .filter((m) => m.pinnedAt !== null)
    .sort((a, b) => (a.pinnedAt ?? "").localeCompare(b.pinnedAt ?? ""));
}

/**
 * プロジェクトの「まずはここを見よう」ボード(SPEC §13.6)。ピンで留めたメモだけを、留めた順にカードで並べる。
 * 開いていないピンには「未読」、開いたあとに変わったピンには「更新」の印を付ける(開くと消える)。
 * ピンが無いときは、ボードの役割を書いて、メモを留めるよう案内する。
 */
export function ProjectBoardCard({
  project,
  className,
}: {
  project: ProjectView;
  className?: string;
}) {
  const memos = useProjectMemos(project.id);
  const prefetch = usePrefetchMemo();
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const pins = pinnedMemos(memos.data?.memos ?? []);
  const unreadCount = pins.filter((m) => m.readState !== null && m.readState !== "READ").length;
  const canCreate = memos.data?.canCreate ?? false;

  return (
    <HomeSection
      data-testid="project-board-card"
      className={className}
      title={
        <span className="flex items-center gap-2">
          <Pin className="size-4" aria-hidden />
          まずはここを見よう
          {unreadCount > 0 && <Badge variant="warning">未読 {unreadCount} 件</Badge>}
        </span>
      }
      description={BOARD_HINT}
      action={
        canCreate && (
          <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
            <Plus />
            メモを作る
          </Button>
        )
      }
    >
      <CardStatus
        isPending={memos.isPending}
        isError={memos.isError}
        refetch={() => memos.refetch()}
      />
      {memos.data && pins.length === 0 && (
        <p className="text-sm text-muted-foreground">
          まだ何も留められていません。初めて参加した人が最初に読む手順やリンク集などをメモに書き、
          「ボードに留める」とここに並びます。
        </p>
      )}
      {pins.length > 0 && (
        <ul className="grid gap-3 sm:grid-cols-2">
          {pins.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                className="flex h-full w-full flex-col gap-1.5 border p-3 text-left hover:bg-accent focus-visible:outline-2"
                onClick={() => setOpenId(m.id)}
                onPointerEnter={() => void prefetch(m.id)}
                onFocus={() => void prefetch(m.id)}
              >
                <span className="flex items-center gap-2 font-medium">
                  <MemoIcon value={m.icon} className="size-4 text-[16px]" />
                  <span className="truncate">{m.title || "無題のメモ"}</span>
                  {m.readState !== null && m.readState !== "READ" && (
                    <Badge variant="warning" className="ml-auto shrink-0">
                      {READ_STATE_LABELS[m.readState]}
                    </Badge>
                  )}
                </span>
                <span className="line-clamp-2 text-xs text-muted-foreground">
                  {m.excerpt || "本文はありません。"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <MemoDialog
        memoId={openId}
        draftId={null}
        open={openId !== null}
        onOpenChange={(o) => !o && setOpenId(null)}
      />
      <MemoDialog
        memoId={null}
        // プロジェクトのメモの新規は下書きを残さない(ホームの下書きの一覧に混ざるため)
        draftId={null}
        projectId={project.id}
        open={creating}
        onOpenChange={setCreating}
      />
    </HomeSection>
  );
}
